import { useCallback, useRef, useSyncExternalStore } from 'react';
import { type FrameViewModel } from 'Lessons/FrameViewModel';
import { type FrontRoyalRootScope } from 'FrontRoyalAngular';
import { angularInjectorProvider } from 'Injector';
import { type SafeApply } from 'SafeApply';

/**
 * Hook to subscribe to a FrameViewModel's state changes.
 * Listens to both 'completed' and 'updated' events to trigger re-renders.
 * Returns the value produced by the provided selector.
 *
 * Whenever any state changes on a frame view model, it has to trigger an 'updated' event to tell
 * react to respond
 *
 * FIXME: This hook indicates that we're doing the wrong thing by using ViewModels in react frames the way we did in
 * legacy frames. We should be defining logic in functions and state in redux rather than defining both in class instances
 * that have to explicitly announce to the world whenever they are changed.
 */
export function useFrameViewModel<T extends FrameViewModel, Result>(
    frameViewModel: T,
    selector: (viewModel: T) => Result,
): Result {
    const snapshotRef = useRef<Result | undefined>(undefined);

    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const handler = () => {
                snapshotRef.current = selector(frameViewModel);
                onStoreChange();

                const safeApply = angularInjectorProvider.get<SafeApply>('safeApply');
                const $rootScope = angularInjectorProvider.get<FrontRoyalRootScope>('$rootScope');
                safeApply($rootScope);
            };
            frameViewModel.on('completed', handler);
            frameViewModel.on('updated', handler);
            return () => {
                frameViewModel.off('completed', handler);
                frameViewModel.off('updated', handler);
            };
        },
        [frameViewModel, selector],
    );

    const getSnapshot = useCallback(() => {
        if (snapshotRef.current === undefined) {
            snapshotRef.current = selector(frameViewModel);
        }
        return snapshotRef.current;
    }, [frameViewModel, selector]);

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
